import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import notificationService from '../services/api/notificationService';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { isSignedIn } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!isSignedIn) return;

    setLoading(true);
    setError(null);

    try {
      const response = await notificationService.getNotifications(options);
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    if (!isSignedIn) return;

    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [isSignedIn]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, []);

  // Dismiss notification
  const dismissNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update count if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  }, [notifications]);

  // Poll for unread count updates
  // Note: SSE is not supported with current Azure Functions + Container Apps setup
  // Using polling as a reliable alternative (every 60 seconds)
  useEffect(() => {
    if (!isSignedIn) return;

    // Poll immediately on mount
    fetchUnreadCount();

    // Set up polling interval (60 seconds)
    const pollInterval = setInterval(() => {
      fetchUnreadCount();
    }, 60000);

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [isSignedIn, fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    if (isSignedIn) {
      fetchNotifications();
    }
  }, [isSignedIn, fetchNotifications]);

  // Add a new notification (for internal use, e.g., from other contexts)
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    addNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
